import { useEffect, useCallback } from "react";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { PageLayoutConfig } from "@/components/layout/index";
import { serializePageConfig } from "@/lib/utils/pageConfigLoader";
import { ItemTypeConfig, ItemType } from "@/pages/unified-items/configs/types";

import { useBaseItemsPage } from "./useBaseItemsPage";
import { useBlueprintsHandlers } from "./useBlueprintsHandlers";
import { useDocumentsHandlers } from "./useDocumentsHandlers";
import { useLibraryHandlers } from "./useLibraryHandlers";
import { useDialogsHandlers } from "./useDialogsHandlers";
import { useFlowsHandlers } from "./useFlowsHandlers";
import { useVariablesHandlers } from "./useVariablesHandlers";

export interface UseUnifiedItemsPageProps {
  itemType: ItemType;
  config: ItemTypeConfig;
}

export function useUnifiedItemsPage({ itemType, config }: UseUnifiedItemsPageProps) {
  // Get base functionality
  const base = useBaseItemsPage({ itemType, config });

  // Get type-specific handlers
  const blueprintsHandlers = useBlueprintsHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
    setIsEditModalOpen: base.setIsEditModalOpen,
    setSelectedBlueprint: base.setSelectedBlueprint,
  });

  const documentsHandlers = useDocumentsHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
    deleteDocument: base.deleteDocument,
    createDocument: base.createDocument,
  });

  const libraryHandlers = useLibraryHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
    setIsEditModalOpen: base.setIsEditModalOpen,
    setIsAddWizardOpen: base.setIsAddWizardOpen,
    setIsCreateWizardOpen: base.setIsCreateWizardOpen,
    setSelectedGlossaryItem: base.setSelectedGlossaryItem,
    setUpdateTrigger: base.setUpdateTrigger,
  });

  const dialogsHandlers = useDialogsHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
    deleteDialogItem: base.deleteDialogItem,
    createDialogItem: base.createDialogItem,
    updateDialogItem: base.updateDialogItem,
    setIsAddWizardOpen: base.setIsAddWizardOpen,
  });

  const flowsHandlers = useFlowsHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
  });

  const variablesHandlers = useVariablesHandlers({
    data: base.data,
    selectedGame: base.selectedGame,
    config,
    setIsAddWizardOpen: base.setIsAddWizardOpen,
  });

  // Select the appropriate handlers based on item type
  const getHandlers = () => {
    switch (itemType) {
      case "blueprints":
        return blueprintsHandlers;
      case "documents":
        return documentsHandlers;
      case "library":
        return libraryHandlers;
      case "dialogs":
        return dialogsHandlers;
      case "flows":
        return flowsHandlers;
      case "variables":
        return variablesHandlers;
      default:
        return blueprintsHandlers;
    }
  };

  const handlers = getHandlers();

  // Track array lengths for stable dependencies
  const cardsLength = base.cards?.length ?? 0;
  const dataLength = base.data?.length ?? 0;
  const tagsLength = base.tags?.length ?? 0;
  const availableSectionsLength = base.availableSections?.length ?? 0;
  const availableFieldsLength = base.availableFields?.length ?? 0;

  // Update page config when cards or selected game changes
  useEffect(() => {
    base.setPageLayoutConfig((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => ({
        ...row,
        layoutConfig: {
          ...row.layoutConfig,
          elements: row.layoutConfig.elements.map((element) => ({
            ...element,
            props: {
              ...element.props,
              title: base.selectedGame
                ? `${config.title} - ${base.selectedGame.name}`
                : config.title,
              cards: base.cards,
              onCardClick: handlers.handleCardClick,
              onDelete: handlers.handleDelete,
              onAdd: handlers.handleAdd,
              onBulkAdd: element.props.onBulkAdd,
              renderCard: config.renderCard
                ? (card: CardData) => config.renderCard(card, base.tags)
                : undefined,
              availableSections: base.availableSections,
              availableFields: base.availableFields,
              rawItems: base.data,
              templateLookup: base.templateLookup,
              onCellValueChange: itemType === "library" ? libraryHandlers.handleCellValueChange : undefined,
            },
          })),
        },
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cardsLength, // Track length instead of array reference
    dataLength, // Track length instead of array reference  
    tagsLength, // Track length instead of array reference
    availableSectionsLength, // Track length instead of array reference
    availableFieldsLength, // Track length instead of array reference
    base.selectedGame?.id, // Track ID instead of object reference
    config.title,
    base.setPageLayoutConfig,
    itemType,
    // Note: Removed array/object references that change identity without content changes
    // Now tracking primitive values (lengths, IDs) for stable comparison
  ]);

  const handlePageConfigChange = useCallback(
    async (newConfig: PageLayoutConfig) => {
      base.setPageLayoutConfig(newConfig);

      if (base.currentPageStructure?.id) {
        try {
          const serializedConfig = serializePageConfig(newConfig);
          await base.updateSection(
            base.currentPageStructure.id,
            config.pageSection,
            serializedConfig
          );
          base.toast({
            title: "Success",
            description: `${config.title} structure saved`,
          });
        } catch (error) {
          console.error(`Error saving ${config.title.toLowerCase()} structure:`, error);
          base.toast({
            title: "Error",
            description: `Failed to save ${config.title.toLowerCase()} structure`,
            variant: "destructive",
          });
        }
      }
    },
    [base.currentPageStructure, base.updateSection, base.toast, config.pageSection, config.title, base.setPageLayoutConfig]
  );

  // Listen for events from AppLayout
  useEffect(() => {
    const handleAddEvent = () => {
      handlers.handleAdd();
    };

    const handleEditLayoutEvent = () => {
      base.setIsEditMode(true);
    };

    window.addEventListener(config.eventName, handleAddEvent);
    window.addEventListener("editLayout", handleEditLayoutEvent);

    return () => {
      window.removeEventListener(config.eventName, handleAddEvent);
      window.removeEventListener("editLayout", handleEditLayoutEvent);
    };
  }, [config.eventName, base.setIsEditMode, handlers.handleAdd]);

  return {
    // Data
    data: base.data,
    isLoading: base.isLoading,
    cards: base.cards,
    selectedGame: base.selectedGame,
    tags: base.tags,
    glossaryBlueprints: base.glossaryBlueprints,

    // Data view mode support
    availableSections: base.availableSections,
    availableFields: base.availableFields,
    templateLookup: base.templateLookup,
    rawItems: base.data,

    // State
    isEditModalOpen: base.isEditModalOpen,
    isAddWizardOpen: base.isAddWizardOpen,
    isCreateWizardOpen: base.isCreateWizardOpen,
    selectedBlueprint: base.selectedBlueprint,
    selectedGlossaryItem: base.selectedGlossaryItem,
    isEditMode: base.isEditMode,

    // Handlers - use type-specific ones
    handleDelete: handlers.handleDelete,
    handleCardClick: handlers.handleCardClick,
    handleAdd: handlers.handleAdd,
    handlePageConfigChange,
    handleGlossaryItemCreated:
      itemType === "library" ? libraryHandlers.handleGlossaryItemCreated : undefined,
    handleGlossaryItemUpdated:
      itemType === "library" ? libraryHandlers.handleGlossaryItemUpdated : undefined,
    handleDocumentCreated:
      itemType === "documents" ? documentsHandlers.handleDocumentCreated : undefined,
    handleSaveBlueprint:
      itemType === "blueprints" ? blueprintsHandlers.handleSaveBlueprint : undefined,
    // Cell value change handler - includes section field support for library items
    handleCellValueChange:
      itemType === "library" ? libraryHandlers.handleCellValueChange : undefined,

    // Setters
    setIsEditModalOpen: base.setIsEditModalOpen,
    setIsAddWizardOpen: base.setIsAddWizardOpen,
    setIsCreateWizardOpen: base.setIsCreateWizardOpen,
    setSelectedBlueprint: base.setSelectedBlueprint,
    setSelectedGlossaryItem: base.setSelectedGlossaryItem,
    setIsEditMode: base.setIsEditMode,

    // Config
    pageLayoutConfig: base.pageLayoutConfig,
    config,
    
    // Refresh handler
    refreshData: base.refreshData,
    
    // Real-time sync
    newItemCount: base.newItemCount,
    clearNewItems: base.clearNewItems,
  };
}

